import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { getCurrentUserUID } from '../../Auth/utilities/authUtils.js';
import { scorePortfolioAgainstChecklist } from '../../AI/utilities/aiLogicService.js';
import Renderer, { createRendererActions } from './Renderer.jsx';
import EditItem from '../EditComponents/EditItem.jsx';
import EditButtons from '../EditComponents/Menus/EditButtons.jsx';
import ClassStyleMenu from '../EditComponents/Menus/ClassStyleMenu.jsx';
import LayerMoveMenu from '../EditComponents/Menus/LayerMoveMenu.jsx';
import PhoneRearrangeMenu from '../EditComponents/Menus/PhoneRearrangeMenu.jsx';
import AiChangePreview from '../EditComponents/Menus/AiChangePreview.jsx';
import SelectionNavigator from '../EditComponents/Menus/SelectionNavigator.jsx';
import EditorModeToggle from '../EditComponents/Menus/EditorModeToggle.jsx';
import FontMenu from '../EditComponents/Menus/FontMenu.jsx';
import GoogleFontLoader from './GoogleFontLoader.jsx';
import ProjectDetailModal from './ProjectDetailModal.jsx';
import ViewItem from '../ViewComponents/ViewItem.jsx';
import AddItemsMenu from '../AddItemComponents/AddItemsMenu.jsx';
import ConfirmationDialog from '../../Common/Feedback/ConfirmationDialog.jsx';
import FeedbackNotice from '../../Common/Feedback/FeedbackNotice.jsx';
import useConfirmation from '../../Common/Feedback/useConfirmation.js';
import { useDragContainer } from '../DragAndDrop/DragContainer.jsx';
import { useEditorDeviceProfile } from '../DragAndDrop/useEditorDragCapability.js';
import { useAuth } from '../../Auth/context/AuthContext';
import {
  createEmptyEditorDocument,
  extractEditorDocument,
  generateCustomCss,
  getElementSelectionContext,
  getElementPath,
  findNodeById,
  findParentById,
  insertNode,
  isTextNode,
  isVoidElement,
  moveNode,
  removeNodeById,
  reorderChild,
  reactAttributesForValidatedNode,
  sanitizeClassDefinitions,
  updateNodeById,
  validateEditorDocument,
  withEditorDocument,
} from '../utilities/htmlEditorUtils.js';
import {
  findEditorNodeElement,
  createSelectionToggleController,
  getFriendlyEditorNodeName,
  getEditorNodeIdFromEvent,
  getToggledEditorSelectionId,
  getSelectionShortcutTarget,
  isSelectionShortcutInput,
} from '../utilities/editorUiUtils.js';
import {
  buildAiPreviewRefinementRequest,
  decideAiPortfolioContext,
  editPortfolioWithCompactPatch,
  editSelectedElement,
  FULL_PAGE_AI_CONTEXT_DECISION,
  generateChildElements,
  generateFullPageStructure,
  HTML_EDITOR_AI_MODEL_NAMES,
  redactBase64FromAiPrompt,
  validateAndRepairEditorJson,
} from '../../AI/NewBlogSystem/utilities/htmlAiUtils.js';
import PortfolioAssistantMenu from '../../AI/NewBlogSystem/components/PortfolioAssistantMenu.jsx';
import {
  AI_QUEUE_STATUS,
  useAiRequestQueue,
} from '../../AI/utilities/aiRequestQueue.js';
import {
  AI_TASK_TYPES,
  AI_WORKFLOW_PHASES,
  createAiTask,
  isGuidedAiTask,
  useAiAssistantState,
} from '../../AI/NewBlogSystem/utilities/aiAssistantState.js';
import {
  buildScoreRecommendationEditPrompt,
  buildPortfolioScoringContext,
  createPortfolioScoreMessagePayload,
  normalizePortfolioScoreResult,
  requestPortfolioScoreWithRetry,
  TARGET_JOB_PORTFOLIO_SCORE_CHECKLIST,
} from '../../AI/NewBlogSystem/utilities/portfolioAiScoring.js';
import { extractPortfolioReferences } from '../utilities/Portfolios/publishedAssetUtils.js';
import {
  containsCollectionNode,
  getOwningCollectionNode,
  hydrateEditorDocumentCollections,
  isManagedEditorDecoration,
  mergeCollectionClassDefinitions,
} from '../utilities/htmlCollectionUtils.js';
import {
  findProjectNodeByRecordId,
  getProjectDetail,
} from '../utilities/projectDetailUtils.js';
import {
  getSerializedByteSize,
  hydrateEditorDocumentImages,
  prepareEditorDocumentForSave,
} from '../utilities/htmlImageReferenceUtils.js';
import {
  EMPTY_LAYER_MOVE_OPTIONS,
  getLayerMoveOptions,
  moveNodeDownLayer,
  moveNodeUpLayer,
} from '../utilities/layerMoveUtils.js';
import { getPortfolioEditElement } from '../utilities/portfolioElementRegistry.jsx';
import useEscapeToClose from '../utilities/useEscapeToClose.js';
import useAnimatedPresence from '../utilities/useAnimatedPresence.js';
import {
  EDITOR_MODES,
  detectAdvancedModeFeatures,
  getSimpleSelectionContext,
  resolveSimpleSelectionId,
} from '../utilities/simpleEditorMode.js';
import '../assets/blogEditor.scss';
import {
  detachFontThemeFromRoot,
  generatePortfolioFontThemeCss,
  withoutFontThemeDefinition,
} from '../utilities/googleFonts.js';
import { getAiChangePreviewSummary } from '../../AI/NewBlogSystem/utilities/aiChangePreviewUtils.js';
import {
  createStoredPortfolioScore,
  PORTFOLIO_SCORE_FIELD,
} from '../utilities/portfolioScoreUtils.js';
import {
  AI_CHAT_HISTORY_FIELD,
  hydrateAiAssistantHistory,
  serializeAiAssistantHistory,
} from '../../AI/NewBlogSystem/utilities/aiAssistantPersistence.js';
import {
  applyPortfolioRecordUpdates,
  countPortfolioRecordUpdates,
  describePortfolioRecordUpdates,
  generatePortfolioRecordUpdates,
} from '../../AI/NewBlogSystem/utilities/portfolioRecordAiUtils.js';
import { createFullPageRegenerationConfirmation } from '../../AI/NewBlogSystem/utilities/aiConfirmationUtils.js';
import {
  createDocumentRevisionTracker,
  resolveSaveStatus,
} from '../utilities/saveStatus.js';
import { moveNodeByVisibleSibling } from '../utilities/phoneRearrangeUtils.js';
import { useScrollAnimations } from '../utilities/animationClasses.js';

const MAX_HISTORY_STEPS = 50;
const MAX_SAFE_PORTFOLIO_BYTES = 900 * 1024;
const AI_TASKS_REQUIRING_REVIEW = new Set([
  AI_TASK_TYPES.GENERATE_PAGE,
  AI_TASK_TYPES.GLOBAL_EDIT,
  AI_TASK_TYPES.FOCUSED_EDIT,
  AI_TASK_TYPES.ADD_SECTION,
  AI_TASK_TYPES.IMPLEMENT_RECOMMENDATION,
  AI_TASK_TYPES.REGENERATE_PAGE,
]);
const isAiQuotaError = (error) => (
  String(error?.code || '').toLowerCase().includes('resource-exhausted') ||
  /(?:429|quota|rate limit)/i.test(String(error?.message || ''))
);
const describeAiContext = (context = {}) => [
  `${context.projects?.length || 0} projects`,
  `${context.skills?.length || 0} skills`,
  `${context.jobs?.length || 0} target jobs`,
  `${context.images?.length || 0} images`,
].join(', ');
const promptMentionsImages = (prompt) => (
  /\b(image|images|photo|photos|picture|pictures|gallery|visual)\b/i.test(String(prompt || ''))
);
const promptMentionsProjects = (prompt) => (
  /\b(project|projects|work|case study|case studies|portfolio)\b/i.test(String(prompt || ''))
);
const promptMentionsSkills = (prompt) => (
  /\b(skill|skills|ability|abilities|technology|technologies|strength|strengths)\b/i.test(String(prompt || ''))
);
const promptMentionsJobs = (prompt) => (
  /\b(job|jobs|role|roles|career|apprenticeship|employer|application|vacancy|fit)\b/i.test(String(prompt || ''))
);
const localAiContextDecision = (prompt) => ({
  includeImages: promptMentionsImages(prompt),
  includeProjects: promptMentionsProjects(prompt),
  includeSkills: promptMentionsSkills(prompt),
  modifyProjects: /\b(?:update|edit|rewrite|rename|change)\b[^.]{0,80}\bprojects?\b|\bprojects?\b[^.]{0,80}\b(?:update|edit|rewrite|rename|change)\b/i.test(String(prompt || '')),
  modifySkills: /\b(?:update|edit|rewrite|rename|change)\b[^.]{0,80}\bskills?\b|\bskills?\b[^.]{0,80}\b(?:update|edit|rewrite|rename|change)\b/i.test(String(prompt || '')),
});
const needsAiPortfolioContext = async ({ prompt, task, selectedNode }) => {
  const localDecision = localAiContextDecision(prompt);
  const selectedReferences = selectedNode
    ? extractPortfolioReferences(selectedNode)
    : { referencedProjects: [], referencedSkills: [] };
  const request = String(prompt || '');
  const selectedRecordIntent = {
    modifyProjects: selectedReferences.referencedProjects.length > 0
      && /\b(title|overview|linked skills?|skill ids?|image descriptions?|caption|wording)\b/i.test(request),
    modifySkills: selectedReferences.referencedSkills.length > 0
      && /\b(title|name|description|icon|wording)\b/i.test(request),
  };
  try {
    const aiDecision = await decideAiPortfolioContext({ prompt, task, selectedNode });
    return Object.fromEntries(Object.keys(localDecision).map((key) => [
      key,
      Boolean(aiDecision[key] || localDecision[key] || selectedRecordIntent[key]),
    ]));
  } catch (error) {
    console.warn('AI portfolio-context check failed; using the local fallback.', error);
    return {
      ...localDecision,
      modifyProjects: localDecision.modifyProjects || selectedRecordIntent.modifyProjects,
      modifySkills: localDecision.modifySkills || selectedRecordIntent.modifySkills,
    };
  }
};
const EMPTY_AI_RECORD_UPDATES = Object.freeze({ projectUpdates: [], skillUpdates: [] });
const withRecordUpdateSummary = (summary, recordUpdates, recordContext) => {
  const recordChanges = countPortfolioRecordUpdates(recordUpdates);
  if (!recordChanges) return { ...summary, recordChanges: 0 };
  return {
    ...summary,
    hasChanges: true,
    recordChanges,
    descriptions: [
      ...(summary.descriptions || []),
      ...describePortfolioRecordUpdates(recordUpdates, recordContext),
    ],
  };
};

const BlogRenderer = ({
  edit = false,
  disallowedItems = [],
  loadPortfolio,
  savePortfolio,
  updatePortfolio = null,
  ai = false,
  aiMethods = null,
}) => {
  const navigate = useNavigate();
  const { userId, portfolioTitle } = useParams();
  const [searchParams] = useSearchParams();
  const isNew = searchParams.get('new') === 'true';
  const location = useLocation();
  const { user } = useAuth();

  const [jsonData, setJsonData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddMenu, setShowAddMenu] = useState(false);
  const [showClassStyleMenu, setShowClassStyleMenu] = useState(false);
  const [showFontMenu, setShowFontMenu] = useState(false);
  const [fontPreviewFamilies, setFontPreviewFamilies] = useState([]);
  const [classStylePreferredClass, setClassStylePreferredClass] = useState('');
  const [showAIMenu, setShowAIMenu] = useState(false);
  const [focusedNodeId, setFocusedNodeId] = useState(null);
  const [openProjectId, setOpenProjectId] = useState(null);
  const [editPanelNodeId, setEditPanelNodeId] = useState(null);
  const [layerMoveTargetId, setLayerMoveTargetId] = useState(null);
  const [showPhoneRearrange, setShowPhoneRearrange] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveFailed, setSaveFailed] = useState(false);
  const [currentRevision, setCurrentRevision] = useState(null);
  const [savedRevision, setSavedRevision] = useState(null);
  const [aiOperation, setAiOperation] = useState('');
  const [aiStatus, setAiStatus] = useState('');
  const [aiChangePreview, setAiChangePreview] = useState(null);
  const [showAiChangePreview, setShowAiChangePreview] = useState(false);
  const [aiConfirmation, setAiConfirmation] = useState(null);
  const [aiQuotaExceeded, setAiQuotaExceeded] = useState(false);
  const [aiComposerTask, setAiComposerTask] = useState(() => createAiTask(AI_TASK_TYPES.GLOBAL_EDIT));
  const [aiComposerDraft, setAiComposerDraft] = useState('');
  const [historyPast, setHistoryPast] = useState([]);
  const [historyFuture, setHistoryFuture] = useState([]);
  const [notice, setNotice] = useState(null);
  const [editorMode, setEditorMode] = useState(EDITOR_MODES.SIMPLE);
  const aiAssistant = useAiAssistantState();
  const aiRequestQueue = useAiRequestQueue();
  const resetAiAssistant = aiAssistant.reset;
  const resetAiRequestQueue = aiRequestQueue.reset;
  const setAiAssistantPhase = aiAssistant.setPhase;
  const restoreAiAssistantHistory = aiAssistant.hydrateHistory;
  const { requestConfirmation, confirmationProps } = useConfirmation();
  const aiInvokerRef = useRef(null);
  const executingQueuedRequestRef = useRef(null);
  const canvasRef = useRef(null);
  const selectionToggleControllerRef = useRef(null);
  if (!selectionToggleControllerRef.current) {
    selectionToggleControllerRef.current = createSelectionToggleController();
  }
  const advancedModeWarningHandledRef = useRef(false);
  const aiHistoryHydratedRef = useRef(false);
  const aiHistorySignatureRef = useRef('');
  const aiHistorySaveQueueRef = useRef(Promise.resolve());
  const revisionTrackerRef = useRef(null);
  if (!revisionTrackerRef.current) {
    revisionTrackerRef.current = createDocumentRevisionTracker();
  }
  const {
    canDrag: deviceCanDrag,
    isCompact,
    isCoarsePointer,
  } = useEditorDeviceProfile();
  const compactElementActions = isCompact || isCoarsePointer;

  const aiEnabled = Boolean(ai);
  const aiBusy = Boolean(aiOperation);
  const saveStatus = resolveSaveStatus({
    isSaving,
    saveFailed,
    currentRevision,
    savedRevision,
  });
  const editorDocument = useMemo(
    () => detachFontThemeFromRoot(extractEditorDocument(jsonData)),
    [jsonData]
  );
  const advancedModeFeatures = useMemo(
    () => detectAdvancedModeFeatures(editorDocument),
    [editorDocument]
  );
  const customClasses = useMemo(
    () => sanitizeClassDefinitions(editorDocument?.classes),
    [editorDocument?.classes]
  );
  const rootChildren = Array.isArray(editorDocument?.root?.children) ? editorDocument.root.children : [];
  const hasPortfolioContent = rootChildren.length > 0;
  const focusedNode = useMemo(
    () => focusedNodeId ? findNodeById(editorDocument?.root, focusedNodeId) : null,
    [editorDocument?.root, focusedNodeId]
  );
  const focusedItemLabel = focusedNode ? getFriendlyEditorNodeName(focusedNode) : '';
  const renderAiMenu = useAnimatedPresence(aiEnabled && showAIMenu);
  const rootDragSupported = edit
    && deviceCanDrag;
  const customCss = useMemo(() => [
    generateCustomCss(withoutFontThemeDefinition(customClasses)),
    generatePortfolioFontThemeCss(customClasses),
  ].filter(Boolean).join('\n\n'), [customClasses]);
  const rootProps = useMemo(
    () => reactAttributesForValidatedNode(editorDocument?.root),
    [editorDocument?.root]
  );
  const rootCanvasClassName = [rootProps.className, 'html-editor-canvas'].filter(Boolean).join(' ');
  const canvasModeClassName = edit ? 'html-editor-canvas-editing' : 'html-editor-canvas-published';
  const resolvedRootCanvasClassName = [rootCanvasClassName, canvasModeClassName].join(' ');
  const RootCanvasElement = isVoidElement(editorDocument?.root?.type)
    ? 'div'
    : getPortfolioEditElement(editorDocument?.root?.type);
  useScrollAnimations(canvasRef, editorDocument?.root);
  const selectionContext = useMemo(() => (
    editorMode === EDITOR_MODES.SIMPLE
      ? getSimpleSelectionContext(editorDocument?.root, focusedNodeId)
      : getElementSelectionContext(editorDocument?.root, focusedNodeId, {
        isSelectable: (node) => !isManagedEditorDecoration(node),
      })
  ), [editorDocument?.root, editorMode, focusedNodeId]);
  const activeEditorPathIds = useMemo(() => {
    const ids = new Set();
    const activeNodeIds = new Set([focusedNodeId, editPanelNodeId].filter(Boolean));
    for (const nodeId of activeNodeIds) {
      getElementPath(editorDocument?.root, nodeId).forEach((node) => ids.add(node.id));
    }
    return ids;
  }, [editPanelNodeId, editorDocument?.root, focusedNodeId]);
  const focusedLayerMoveOptions = useMemo(
    () => editorMode === EDITOR_MODES.ADVANCED && focusedNodeId
      ? getLayerMoveOptions(editorDocument?.root, focusedNodeId)
      : EMPTY_LAYER_MOVE_OPTIONS,
    [editorDocument?.root, editorMode, focusedNodeId]
  );
  const openProject = useMemo(() => {
    if (!openProjectId) return null;
    return getProjectDetail(findProjectNodeByRecordId(editorDocument?.root, openProjectId));
  }, [editorDocument?.root, openProjectId]);

  useEffect(() => {
    if (
      showAddMenu
      || showClassStyleMenu
      || showAIMenu
      || editPanelNodeId
      || layerMoveTargetId
      || showPhoneRearrange
    ) {
      setShowFontMenu(false);
    }
  }, [
    editPanelNodeId,
    layerMoveTargetId,
    showAIMenu,
    showAddMenu,
    showClassStyleMenu,
    showPhoneRearrange,
  ]);

  useEffect(() => {
    advancedModeWarningHandledRef.current = false;
    aiHistoryHydratedRef.current = false;
    aiHistorySignatureRef.current = '';
    setShowAIMenu(false);
    setAiChangePreview(null);
    setShowAiChangePreview(false);
    setAiConfirmation(null);
    setAiComposerTask(createAiTask(AI_TASK_TYPES.GLOBAL_EDIT));
    setAiComposerDraft('');
    resetAiRequestQueue();
    resetAiAssistant();
  }, [portfolioTitle, resetAiAssistant, resetAiRequestQueue, userId]);
  const layerMoveOptions = useMemo(
    () => layerMoveTargetId
      ? getLayerMoveOptions(editorDocument?.root, layerMoveTargetId)
      : EMPTY_LAYER_MOVE_OPTIONS,
    [editorDocument?.root, layerMoveTargetId]
  );

  const selectEditorNode = useCallback((nodeId, { scroll = false } = {}) => {
    const resolvedNodeId = editorMode === EDITOR_MODES.SIMPLE
      ? resolveSimpleSelectionId(editorDocument?.root, nodeId)
      : nodeId;
    setFocusedNodeId(resolvedNodeId || null);
    setEditPanelNodeId((current) => current ? (resolvedNodeId || null) : null);

    if (!resolvedNodeId || !scroll) return;
    window.requestAnimationFrame(() => {
      const selectedElement = findEditorNodeElement(resolvedNodeId);
      selectedElement?.scrollIntoView?.({ behavior: 'smooth', block: 'nearest' });
    });
  }, [editorDocument?.root, editorMode]);

  const redirectToLogin = useCallback(() => {
    const redirectPath = encodeURIComponent(`${location.pathname}${location.search}${location.hash}`);
    navigate(`/login?redirectPath=${redirectPath}`, { replace: true });
  }, [location.hash, location.pathname, location.search, navigate]);

  const isAuthRelatedLoadError = useCallback((message = '') => {
    const normalizedMessage = message.toLowerCase();
    return (
      normalizedMessage.includes('unauthenticated') ||
      normalizedMessage.includes('permission') ||
      normalizedMessage.includes('insufficient')
    );
  }, []);

  const setVersionedJsonData = useCallback((nextData, {
    revision = null,
    markSaved = false,
    resetSaved = false,
  } = {}) => {
    if (!nextData) return null;

    const nextRevision = revisionTrackerRef.current.register(nextData, revision);
    setCurrentRevision(nextRevision);
    setSaveFailed(false);
    if (markSaved) setSavedRevision(nextRevision);
    else if (resetSaved) setSavedRevision(null);
    setJsonData(nextData);
    return nextRevision;
  }, []);

  const cloneWithCurrentRevision = useCallback((data) => {
    return revisionTrackerRef.current.cloneCurrent(data);
  }, []);

  const applyPortfolioState = useCallback((nextJsonData) => {
    if (!nextJsonData) {
      return;
    }

    const nextValidatedData = withEditorDocument(
      nextJsonData,
      detachFontThemeFromRoot(extractEditorDocument(nextJsonData))
    );

    if (!edit || !jsonData) {
      setVersionedJsonData(nextValidatedData);
      return;
    }

    setHistoryPast((previous) => {
      const snapshot = cloneWithCurrentRevision(jsonData);
      const nextHistory = [...previous, snapshot];
      return nextHistory.slice(Math.max(0, nextHistory.length - MAX_HISTORY_STEPS));
    });
    setHistoryFuture([]);
    setVersionedJsonData(nextValidatedData);
  }, [cloneWithCurrentRevision, edit, jsonData, setVersionedJsonData]);

  const setEditorDocument = useCallback((updater) => {
    if (!jsonData) {
      return;
    }

    const currentDocument = detachFontThemeFromRoot(extractEditorDocument(jsonData));
    const nextDocument = typeof updater === 'function' ? updater(currentDocument) : updater;
    const validatedDocument = validateEditorDocument(nextDocument).document;
    const documentWithCollectionClasses = containsCollectionNode(validatedDocument.root)
      ? { ...validatedDocument, classes: mergeCollectionClassDefinitions(validatedDocument.classes) }
      : validatedDocument;
    applyPortfolioState(withEditorDocument(jsonData, documentWithCollectionClasses));
  }, [applyPortfolioState, jsonData]);

  const loadPortfolioData = useCallback(async () => {
    if (!portfolioTitle) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      if (isNew) {
        restoreAiAssistantHistory({ runs: [], messages: [] });
        aiHistoryHydratedRef.current = true;
        setVersionedJsonData({
          title: portfolioTitle,
          content: createEmptyEditorDocument(),
        }, { resetSaved: true });
        setHistoryPast([]);
        setHistoryFuture([]);
        return;
      }

      if (!userId) {
        throw new Error('Please sign in to open this portfolio.');
      }

      const data = await loadPortfolio(userId, portfolioTitle);

      if (data) {
        const savedAiHistory = hydrateAiAssistantHistory(data[AI_CHAT_HISTORY_FIELD]);
        aiHistorySignatureRef.current = JSON.stringify(
          serializeAiAssistantHistory(savedAiHistory)
        );
        restoreAiAssistantHistory(savedAiHistory);
        aiHistoryHydratedRef.current = true;
        const collectionDocument = await hydrateEditorDocumentCollections(extractEditorDocument(data), userId);
        const hydratedDocument = await hydrateEditorDocumentImages(collectionDocument, userId);
        setVersionedJsonData(
          withEditorDocument(data, hydratedDocument),
          { markSaved: true }
        );
        setHistoryPast([]);
        setHistoryFuture([]);
      } else {
        navigate('/dashboard', { replace: true });
      }
    } catch (err) {
      console.error('Error loading portfolio:', err);
      if (!user && isAuthRelatedLoadError(err?.message || '')) {
        redirectToLogin();
        return;
      }
      setError(err.message || 'We could not open this portfolio. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [
    isNew,
    portfolioTitle,
    redirectToLogin,
    isAuthRelatedLoadError,
    user,
    userId,
    navigate,
    loadPortfolio,
    setVersionedJsonData,
    restoreAiAssistantHistory,
  ]);

  useEffect(() => {
    void loadPortfolioData();
  }, [loadPortfolioData]);

  useEffect(() => {
    if (!edit || !aiHistoryHydratedRef.current || !jsonData) return;

    const history = serializeAiAssistantHistory({
      runs: aiAssistant.runs,
      messages: aiAssistant.messages,
    });
    const signature = JSON.stringify(history);
    if (signature === aiHistorySignatureRef.current) return;

    aiHistorySignatureRef.current = signature;
    const storedHistory = { ...history, updatedAt: new Date().toISOString() };
    setJsonData((current) => current
      ? { ...current, [AI_CHAT_HISTORY_FIELD]: storedHistory }
      : current);

    if (isNew || typeof updatePortfolio !== 'function') return;

    aiHistorySaveQueueRef.current = aiHistorySaveQueueRef.current
      .catch(() => undefined)
      .then(() => updatePortfolio(userId, portfolioTitle, {
        [AI_CHAT_HISTORY_FIELD]: storedHistory,
      }))
      .catch((historySaveError) => {
        console.error('Error saving AI chat history:', historySaveError);
        setNotice({
          variant: 'warning',
          message: 'Your latest AI conversation could not be saved.',
        });
      });
  }, [
    aiAssistant.messages,
    aiAssistant.runs,
    edit,
    isNew,
    jsonData,
    portfolioTitle,
    updatePortfolio,
    userId,
  ]);

  useEffect(() => {
    if (!edit || !focusedNodeId) return undefined;

    const handleSelectionShortcut = (event) => {
      const menuIsOpen = showAddMenu || showClassStyleMenu || showFontMenu || showAIMenu || Boolean(editPanelNodeId)
        || Boolean(document.querySelector(
          '.outer-fixed, .left-menu.show, .right-menu.show, .ai-change-preview-backdrop'
        ));
      if (menuIsOpen || isSelectionShortcutInput(event.target)) return;

      const shortcut = getSelectionShortcutTarget({
        key: event.key,
        altKey: event.altKey,
        context: selectionContext,
      });
      if (!shortcut) return;

      event.preventDefault();
      if (shortcut.action === 'clear') {
        selectEditorNode(null);
      } else {
        selectEditorNode(shortcut.nodeId, { scroll: true });
      }
    };

    window.addEventListener('keydown', handleSelectionShortcut);
    return () => window.removeEventListener('keydown', handleSelectionShortcut);
  }, [
    edit,
    editPanelNodeId,
    focusedNodeId,
    selectEditorNode,
    selectionContext,
    showAIMenu,
    showAddMenu,
    showClassStyleMenu,
    showFontMenu,
  ]);

  useEffect(() => {
    if (focusedNodeId && selectionContext.path.length === 0) {
      selectEditorNode(null);
    }
  }, [focusedNodeId, selectEditorNode, selectionContext.path.length]);

  const savePortfolioData = useCallback(async (dataToSave) => {
    const revisionBeingSaved = revisionTrackerRef.current.currentRevision;
    setIsSaving(true);
    setSaveFailed(false);
    setError(null);

    try {
      if (!userId) {
        throw new Error('Please sign in before saving your portfolio.');
      }

      const prepared = prepareEditorDocumentForSave(extractEditorDocument(dataToSave));
      const compactData = withEditorDocument(dataToSave, prepared.document);
      const serializedBytes = getSerializedByteSize(compactData);

      if (serializedBytes > MAX_SAFE_PORTFOLIO_BYTES) {
        throw new Error(`This portfolio is too large to save (${Math.ceil(serializedBytes / 1024)} KB). Shorten unusually long text or simplify detailed styling, then try again.`);
      }

      const result = await savePortfolio(userId, portfolioTitle, compactData);
      if (result?.success === false) {
        throw new Error(result.error || 'We could not save your portfolio. Please try again.');
      }
      setSavedRevision(revisionBeingSaved);
      return { success: true, ...result };
    } catch (err) {
      console.error('Error saving portfolio:', err);
      setSaveFailed(true);
      setError(err.message || 'We could not save your portfolio. Please try again.');
      return { success: false, error: err.message || 'We could not save your portfolio. Please try again.' };
    } finally {
      setIsSaving(false);
    }
  }, [portfolioTitle, userId, savePortfolio]);

  const {
    dragEnabled: rootDragEnabled,
    captureItemPositions,
    setDragEnabled: setRootDragEnabled,
    onDragStart,
    onDragOver,
    onDrop,
    onDragEnd,
    getDragClassName,
    setItemRef,
  } = useDragContainer({
    items: rootChildren,
    edit: rootDragSupported,
    animationDependency: jsonData,
  });

  const changeEditorMode = useCallback((nextMode) => {
    if (!Object.values(EDITOR_MODES).includes(nextMode) || nextMode === editorMode) return;

    const nextFocusedId = nextMode === EDITOR_MODES.SIMPLE
      ? resolveSimpleSelectionId(editorDocument?.root, focusedNodeId)
      : focusedNodeId;
    setEditorMode(nextMode);
    setFocusedNodeId(nextFocusedId || null);
    setEditPanelNodeId(null);
    setShowAddMenu(false);
    setShowClassStyleMenu(false);
    setShowFontMenu(false);
    setShowAIMenu(false);
    setLayerMoveTargetId(null);
    setShowPhoneRearrange(false);
    setRootDragEnabled(true);
  }, [editorDocument?.root, editorMode, focusedNodeId, setRootDragEnabled]);

  useEffect(() => {
    if (
      !edit
      || isLoading
      || editorMode !== EDITOR_MODES.SIMPLE
      || !advancedModeFeatures.hasAdvancedFeatures
      || advancedModeWarningHandledRef.current
      || confirmationProps.open
    ) {
      return;
    }

    advancedModeWarningHandledRef.current = true;
    const featureSummary = advancedModeFeatures.labels.join(', ');
    requestConfirmation({
      title: 'This page uses Advanced Mode features',
      message: `This page includes ${featureSummary}. You can make common changes in Simple mode, but the complete page can only be edited in Advanced Mode.`,
      confirmLabel: 'Switch to Advanced',
      cancelLabel: 'Stay in Simple',
      variant: 'warning',
      onConfirm: () => changeEditorMode(EDITOR_MODES.ADVANCED),
    });
  }, [
    advancedModeFeatures,
    changeEditorMode,
    confirmationProps.open,
    edit,
    editorMode,
    isLoading,
    requestConfirmation,
  ]);

  const handleCanvasClick = useCallback((event) => {
    if (!edit) return;

    if (event.target instanceof Element && event.target.closest('a')) {
      event.preventDefault();
    }

    const rawNodeId = getEditorNodeIdFromEvent(event);
    const clickedNodeId = editorMode === EDITOR_MODES.SIMPLE
      ? resolveSimpleSelectionId(editorDocument?.root, rawNodeId)
      : rawNodeId;

    selectionToggleControllerRef.current.schedule(() => {
      selectEditorNode(getToggledEditorSelectionId(focusedNodeId, clickedNodeId));
    }, event.detail);
  }, [edit, editorDocument?.root, editorMode, focusedNodeId, selectEditorNode]);

  const handleCanvasDoubleClick = useCallback((event) => {
    if (!edit) return;

    selectionToggleControllerRef.current.cancel();

    const rawNodeId = getEditorNodeIdFromEvent(event);
    const nodeId = editorMode === EDITOR_MODES.SIMPLE
      ? resolveSimpleSelectionId(editorDocument?.root, rawNodeId)
      : rawNodeId;
    if (!nodeId) return;

    event.preventDefault();
    setFocusedNodeId(nodeId);
    setEditPanelNodeId(nodeId);
    setShowAddMenu(false);
    setShowClassStyleMenu(false);
    setShowAIMenu(false);
    setLayerMoveTargetId(null);
    setRootDragEnabled(false);
  }, [edit, editorDocument?.root, editorMode, setRootDragEnabled]);

  useEffect(() => () => selectionToggleControllerRef.current?.cancel(), []);

  const openPublishedProject = useCallback((event) => {
    if (edit || !(event.target instanceof Element)) return;
    if (event.target.closest('[data-project-carousel-control="true"]')) return;
    if (event.target.closest('a[data-project-field="project-url"]')) return;

    const projectCard = event.target.closest(
      '[data-managed-role="project-item"][data-project-id]'
    );
    const projectId = projectCard?.getAttribute('data-project-id');
    if (projectId) setOpenProjectId(projectId);
  }, [edit]);

  const openPublishedProjectFromKeyboard = useCallback((event) => {
    if (edit || !['Enter', ' '].includes(event.key) || !(event.target instanceof Element)) return;
    const projectCard = event.target.closest(
      '[data-managed-role="project-item"][data-project-id]'
    );
    if (!projectCard || event.target !== projectCard) return;

    event.preventDefault();
    setOpenProjectId(projectCard.getAttribute('data-project-id'));
  }, [edit]);

  useEffect(() => {
    if (openProjectId && !openProject) setOpenProjectId(null);
  }, [openProject, openProjectId]);

  const setRootContent = (nextContent) => {
    setEditorDocument((current) => ({
      ...current,
      root: {
        ...current.root,
        children: nextContent,
      },
    }));
  };

  const moveEditorNode = useCallback((nodeId, targetId, position) => {
    setEditorDocument((current) => ({
      ...current,
      root: moveNode(current.root, nodeId, targetId, position),
    }));
    selectEditorNode(nodeId);
  }, [selectEditorNode, setEditorDocument]);

  const updateEditorNode = useCallback((nodeId, updater) => {
    setEditorDocument((current) => ({
      ...current,
      root: updateNodeById(current.root, nodeId, updater),
    }));
  }, [setEditorDocument]);

  const insertEditorNode = useCallback((targetId, node, position = 'child') => {
    setEditorDocument((current) => ({
      ...current,
      root: insertNode(current.root, targetId, node, position),
    }));
  }, [setEditorDocument]);

  const removeEditorNode = useCallback((nodeId) => {
    setEditorDocument((current) => ({
      ...current,
      root: removeNodeById(current.root, nodeId),
    }));
    selectEditorNode(null);
  }, [selectEditorNode, setEditorDocument]);

  const reorderEditorNode = useCallback((nodeId, offset) => {
    setEditorDocument((current) => {
      const parent = findParentById(current.root, nodeId);
      const fromIndex = parent?.children?.findIndex((child) => child.id === nodeId) ?? -1;
      if (!parent || fromIndex < 0) return current;

      return {
        ...current,
        root: reorderChild(current.root, parent.id, fromIndex, fromIndex + offset),
      };
    });
  }, [setEditorDocument]);

  const closeLayerMoveMenu = useCallback(() => {
    setLayerMoveTargetId(null);
    setRootDragEnabled(true);
  }, [setRootDragEnabled]);

  const discardAiChangePreview = useCallback(() => {
    const pendingPreview = aiChangePreview;
    setShowAiChangePreview(false);
    setAiChangePreview(null);
    pendingPreview?.onDiscard?.();
    aiAssistant.addMessage('assistant', 'The proposed changes were discarded. Your current portfolio was kept.');
    aiAssistant.completeRun('Changes discarded');
    if (aiRequestQueue.activeRequest?.status === AI_QUEUE_STATUS.AWAITING_REVIEW) {
      aiRequestQueue.complete(aiRequestQueue.activeRequest.id);
    }
    setAiComposerTask(createAiTask(AI_TASK_TYPES.GLOBAL_EDIT));
    setAiComposerDraft('');
    setRootDragEnabled(true);
  }, [aiAssistant, aiChangePreview, aiRequestQueue, setRootDragEnabled]);

  useEscapeToClose(
    showAIMenu
      || showClassStyleMenu
      || showFontMenu
      || Boolean(layerMoveTargetId),
    () => {
      if (layerMoveTargetId) closeLayerMoveMenu();
      else if (showFontMenu) setShowFontMenu(false);
      else if (showClassStyleMenu) setShowClassStyleMenu(false);
      else if (showAIMenu) {
        if (aiChangePreview) discardAiChangePreview();
        setShowAIMenu(false);
        aiAssistant.close();
        aiInvokerRef.current?.focus?.();
      }
      setRootDragEnabled(true);
    }
  );

  const openPhoneRearrangeMenu = useCallback((nodeId) => {
    if (!compactElementActions) return;
    setFocusedNodeId(nodeId || focusedNodeId);
    setShowPhoneRearrange(true);
    setEditPanelNodeId(null);
    setShowAddMenu(false);
    setShowClassStyleMenu(false);
    setShowFontMenu(false);
    setShowAIMenu(false);
    setLayerMoveTargetId(null);
    setRootDragEnabled(false);
  }, [compactElementActions, focusedNodeId, setRootDragEnabled]);

  const closePhoneRearrangeMenu = useCallback(() => {
    setShowPhoneRearrange(false);
    setRootDragEnabled(true);
  }, [setRootDragEnabled]);

  useEffect(() => {
    if (!compactElementActions && showPhoneRearrange) {
      closePhoneRearrangeMenu();
    }
  }, [closePhoneRearrangeMenu, compactElementActions, showPhoneRearrange]);

  const openLayerMoveMenu = useCallback((nodeId) => {
    setFocusedNodeId(nodeId);
    setLayerMoveTargetId(nodeId);
    setEditPanelNodeId(null);
    setShowAddMenu(false);
    setShowClassStyleMenu(false);
    setShowFontMenu(false);
    setShowAIMenu(false);
    setRootDragEnabled(false);
  }, [setRootDragEnabled]);

  const openAssistantTask = useCallback((task, options = {}) => {
    if (!showAIMenu && typeof document !== 'undefined') {
      aiInvokerRef.current = document.activeElement;
    }
    const targetNodeId = task?.nodeId || null;
    if (targetNodeId) setFocusedNodeId(targetNodeId);
    setShowAIMenu(true);
    setEditPanelNodeId(null);
    setShowAddMenu(false);
    setShowClassStyleMenu(false);
    setShowFontMenu(false);
    setLayerMoveTargetId(null);
    setShowPhoneRearrange(false);
    setAiComposerTask(task);
    setAiComposerDraft(options.draft || '');
    aiAssistant.reopen();
    setRootDragEnabled(false);
  }, [aiAssistant, setRootDragEnabled, showAIMenu]);

  const reopenAiAssistant = useCallback(() => {
    if (!showAIMenu && typeof document !== 'undefined') {
      aiInvokerRef.current = document.activeElement;
    }
    const targetNodeId = aiComposerTask?.nodeId || null;
    if (targetNodeId && findNodeById(editorDocument?.root, targetNodeId)) {
      setFocusedNodeId(targetNodeId);
    }
    setShowAIMenu(true);
    setEditPanelNodeId(null);
    setShowAddMenu(false);
    setShowClassStyleMenu(false);
    setShowFontMenu(false);
    setLayerMoveTargetId(null);
    setShowPhoneRearrange(false);
    if (!hasPortfolioContent && aiComposerTask?.type === AI_TASK_TYPES.GLOBAL_EDIT
      && !aiComposerDraft.trim()) {
      setAiComposerTask(createAiTask(AI_TASK_TYPES.GENERATE_PAGE));
    }
    aiAssistant.reopen();
    setRootDragEnabled(false);
  }, [
    aiAssistant,
    aiComposerDraft,
    aiComposerTask?.nodeId,
    aiComposerTask?.type,
    editorDocument?.root,
    hasPortfolioContent,
    setRootDragEnabled,
    showAIMenu,
  ]);

  const createFocusedAiTask = useCallback((nodeId) => {
    if (!nodeId) return;
    const collectionTarget = getOwningCollectionNode(editorDocument?.root, nodeId);
    const targetNodeId = collectionTarget?.id || nodeId;
    const targetNode = findNodeById(editorDocument?.root, targetNodeId);
    if (!targetNode) return;
    return createAiTask(AI_TASK_TYPES.FOCUSED_EDIT, {
      nodeId: targetNodeId,
      label: `Edit ${getFriendlyEditorNodeName(targetNode)}`,
    });
  }, [editorDocument?.root]);

  const openFocusedAiMenu = useCallback((nodeId) => {
    const task = createFocusedAiTask(nodeId);
    if (task) openAssistantTask(task);
  }, [createFocusedAiTask, openAssistantTask]);

  useEffect(() => {
    if (!showAIMenu) return;

    if (!focusedNodeId) {
      const canReturnToPortfolioEdit = Boolean(
        aiComposerTask?.type === AI_TASK_TYPES.FOCUSED_EDIT
      );
      if (canReturnToPortfolioEdit) {
        setAiComposerTask(createAiTask(AI_TASK_TYPES.GLOBAL_EDIT));
      }
      return;
    }

    const task = createFocusedAiTask(focusedNodeId);
    if (!task || task.key === aiComposerTask?.key
      || aiComposerTask?.type !== AI_TASK_TYPES.FOCUSED_EDIT) return;
    setAiComposerTask(task);
  }, [
    aiComposerTask?.key,
    aiComposerTask?.type,
    createFocusedAiTask,
    focusedNodeId,
    showAIMenu,
  ]);

  const openContextualAiAssistant = useCallback(() => {
    if (focusedNodeId && findNodeById(editorDocument?.root, focusedNodeId)) {
      openFocusedAiMenu(focusedNodeId);
      return;
    }
    reopenAiAssistant();
  }, [editorDocument?.root, focusedNodeId, openFocusedAiMenu, reopenAiAssistant]);

  const moveEditorNodeUpLayer = useCallback((nodeId) => {
    const nextRoot = moveNodeUpLayer(editorDocument?.root, nodeId);
    if (!nextRoot || nextRoot === editorDocument?.root) return false;

    setEditorDocument({ ...editorDocument, root: nextRoot });
    selectEditorNode(nodeId, { scroll: true });
    return true;
  }, [editorDocument, selectEditorNode, setEditorDocument]);

  const moveEditorNodeDownLayer = useCallback((parentId, slotId) => {
    if (!layerMoveTargetId) return false;

    const nextRoot = moveNodeDownLayer(editorDocument?.root, layerMoveTargetId, parentId, slotId);
    if (!nextRoot || nextRoot === editorDocument?.root) return false;

    const movedNodeId = layerMoveTargetId;
    setEditorDocument({ ...editorDocument, root: nextRoot });
    closeLayerMoveMenu();
    selectEditorNode(movedNodeId, { scroll: true });
    return true;
  }, [closeLayerMoveMenu, editorDocument, layerMoveTargetId, selectEditorNode, setEditorDocument]);

  const reorderPhoneEditorNode = useCallback((nodeId, offset) => {
    let moved = false;
    setEditorDocument((current) => {
      const nextRoot = moveNodeByVisibleSibling(current.root, nodeId, offset);
      moved = nextRoot !== current.root;
      return moved ? { ...current, root: nextRoot } : current;
    });
    if (moved) selectEditorNode(nodeId);
    return moved;
  }, [selectEditorNode, setEditorDocument]);

  const movePhoneEditorNodeInto = useCallback((nodeId, parentId, slotId) => {
    let moved = false;
    setEditorDocument((current) => {
      const nextRoot = moveNodeDownLayer(current.root, nodeId, parentId, slotId);
      moved = nextRoot !== current.root;
      return moved ? { ...current, root: nextRoot } : current;
    });
    if (moved) selectEditorNode(nodeId);
    return moved;
  }, [selectEditorNode, setEditorDocument]);

  useEffect(() => {
    if (layerMoveTargetId && focusedNodeId !== layerMoveTargetId) {
      closeLayerMoveMenu();
    }
  }, [closeLayerMoveMenu, focusedNodeId, layerMoveTargetId]);

  const onUndo = () => {
    if (!edit || historyPast.length === 0 || !jsonData) {
      return;
    }

    const previous = historyPast[historyPast.length - 1];
    const previousRevision = revisionTrackerRef.current.revisionOf(previous);
    const currentSnapshot = cloneWithCurrentRevision(jsonData);
    setHistoryPast((past) => past.slice(0, -1));
    setHistoryFuture((future) => [currentSnapshot, ...future].slice(0, MAX_HISTORY_STEPS));
    setVersionedJsonData(previous, { revision: previousRevision });
  };

  const onRedo = () => {
    if (!edit || historyFuture.length === 0 || !jsonData) {
      return;
    }

    const [next, ...remaining] = historyFuture;
    const nextRevision = revisionTrackerRef.current.revisionOf(next);
    const currentSnapshot = cloneWithCurrentRevision(jsonData);
    setHistoryFuture(remaining);
    setHistoryPast((past) => [...past, currentSnapshot].slice(Math.max(0, past.length + 1 - MAX_HISTORY_STEPS)));
    setVersionedJsonData(next, { revision: nextRevision });
  };

  const loadAiRefinementContext = useCallback(async ({
    request,
    document,
    selectedNode = null,
    task,
  }) => {
    const referenceSource = selectedNode || document;
    const references = extractPortfolioReferences(referenceSource);
    const contextDecision = await needsAiPortfolioContext({
      prompt: request,
      task,
      selectedNode,
    });

    if (typeof aiMethods?.getGenerationContext !== 'function') {
      return { recordUpdateIntent: contextDecision };
    }
    const context = (await aiMethods.getGenerationContext({
      userId,
      includeProjects: contextDecision.includeProjects
        || contextDecision.modifyProjects
        || references.referencedProjects.length > 0,
      includeSkills: contextDecision.includeSkills
        || contextDecision.modifySkills
        || contextDecision.modifyProjects
        || references.referencedSkills.length > 0,
      includeJobs: promptMentionsJobs(request),
      includeImages: contextDecision.includeImages,
      projectIds: contextDecision.includeProjects || contextDecision.modifyProjects
        ? undefined
        : references.referencedProjects,
      skillIds: contextDecision.includeSkills || contextDecision.modifySkills || contextDecision.modifyProjects
        ? undefined
        : references.referencedSkills,
      hydrateImageDescriptions: false,
    })) || {};
    return { ...context, recordUpdateIntent: contextDecision };
  }, [aiMethods, userId]);

  const runGlobalAiEditWorkflow = useCallback(async ({
    request,
    sourceDocument,
    task = 'compact portfolio edit',
    previousRequests = [],
  }) => {
    setAiStatus('Checking which portfolio details are needed...');
    const context = await loadAiRefinementContext({
      request,
      document: sourceDocument,
      task,
    });
    const effectiveRequest = previousRequests.length
      ? buildAiPreviewRefinementRequest({ request, previousRequests })
      : request;

    setAiStatus(`Generating with ${describeAiContext(context)}...`);
    const editedDocument = await editPortfolioWithCompactPatch({
      prompt: effectiveRequest,
      portfolio: withEditorDocument(jsonData, sourceDocument),
      images: context.images || [],
      projects: context.projects || [],
      skills: context.skills || [],
      jobs: context.jobs || [],
      fallbackTitle: jsonData?.title || portfolioTitle,
      onRetry: (error) => setAiStatus(
        error?.code === 'AI_MAX_TOKENS'
          ? 'The page is large. Retrying with the next AI model...'
          : 'The first result was incomplete. Retrying with a smaller edit...'
      ),
    });

    let recordUpdates = EMPTY_AI_RECORD_UPDATES;
    const recordUpdatesSpecified = Boolean(
      context.recordUpdateIntent?.modifyProjects || context.recordUpdateIntent?.modifySkills
    );
    if (recordUpdatesSpecified) {
      setAiStatus('Preparing changes to your saved projects and skills...');
      recordUpdates = await generatePortfolioRecordUpdates({
        prompt: effectiveRequest,
        projects: context.projects || [],
        skills: context.skills || [],
        onRetry: () => setAiStatus('Retrying the saved project and skill changes...'),
      });
    }

    setAiStatus('Updating your projects and skills...');
    const hydratedDocument = await hydrateEditorDocumentCollections(editedDocument, userId);
    setAiStatus('Preparing your preview...');
    return {
      context,
      document: validateAndRepairEditorJson(hydratedDocument),
      recordUpdates,
      recordContext: {
        projects: context.projects || [],
        skills: context.skills || [],
      },
      recordUpdatesSpecified,
    };
  }, [
    jsonData,
    loadAiRefinementContext,
    portfolioTitle,
    userId,
  ]);

  const queueAiChangePreview = useCallback(({
    title,
    beforeDocument,
    afterDocument,
    focusedNodeId: previewFocusedNodeId = null,
    recordUpdates = EMPTY_AI_RECORD_UPDATES,
    recordContext = { projects: [], skills: [] },
    onApply,
    onDiscard,
    onRefine,
    requestHistory = [],
  }) => {
    const previewId = `ai-preview-${Date.now()}`;
    setAiConfirmation(null);
    const documentSummary = getAiChangePreviewSummary(beforeDocument, afterDocument, {
      focusedNodeId: previewFocusedNodeId,
    });
    setAiChangePreview({
      id: previewId,
      title,
      beforeDocument,
      afterDocument,
      focusedNodeId: previewFocusedNodeId,
      summary: withRecordUpdateSummary(documentSummary, recordUpdates, recordContext),
      recordUpdates,
      recordContext,
      onApply,
      onDiscard,
      onRefine,
      requestHistory,
    });
    setAiAssistantPhase(AI_WORKFLOW_PHASES.REVIEW, { previewId, error: '' });
    setShowAiChangePreview(false);
    setRootDragEnabled(false);
  }, [setAiAssistantPhase, setRootDragEnabled]);

  const applyAiChangePreview = useCallback(async () => {
    const pendingPreview = aiChangePreview;
    if (!pendingPreview) return;
    try {
      let approvedDocument = pendingPreview.afterDocument;
      const recordChangeCount = countPortfolioRecordUpdates(pendingPreview.recordUpdates);
      if (recordChangeCount) {
        setAiOperation('apply-record-updates');
        setAiStatus('Updating your saved projects and skills...');
        await applyPortfolioRecordUpdates({
          userId,
          updates: pendingPreview.recordUpdates,
          projects: pendingPreview.recordContext?.projects || [],
          skills: pendingPreview.recordContext?.skills || [],
          updateProject: aiMethods?.updateProject,
          updateSkill: aiMethods?.updateSkill,
        });
        approvedDocument = await hydrateEditorDocumentCollections(approvedDocument, userId);
      }
      await pendingPreview.onApply?.(approvedDocument);
      if (
        aiAssistant.activeTask?.scoreMessageId
        && Number.isInteger(aiAssistant.activeTask.recommendationIndex)
      ) {
        aiAssistant.markRecommendationApplied(
          aiAssistant.activeTask.scoreMessageId,
          aiAssistant.activeTask.recommendationIndex
        );
      }
      setShowAiChangePreview(false);
      setAiChangePreview(null);
      aiAssistant.addMessage(
        'assistant',
        recordChangeCount
          ? `The preview and ${recordChangeCount} saved record change${recordChangeCount === 1 ? '' : 's'} were applied.`
          : 'The preview was applied. You can use Undo to restore the previous version.'
      );
      aiAssistant.completeRun('Changes applied');
      if (aiRequestQueue.activeRequest?.status === AI_QUEUE_STATUS.AWAITING_REVIEW) {
        aiRequestQueue.complete(aiRequestQueue.activeRequest.id);
      }
      setAiComposerTask(createAiTask(AI_TASK_TYPES.GLOBAL_EDIT));
      setAiComposerDraft('');
      setRootDragEnabled(true);
    } catch (recordApplyError) {
      console.error('Error applying AI project or skill updates:', recordApplyError);
      setNotice({
        variant: 'danger',
        message: recordApplyError?.message || 'The saved project or skill changes could not be applied.',
      });
      aiAssistant.addMessage(
        'error',
        'The proposed changes were not fully applied. Review the preview and try again.'
      );
    } finally {
      setAiOperation('');
      setAiStatus('');
    }
  }, [aiAssistant, aiChangePreview, aiMethods, aiRequestQueue, setRootDragEnabled, userId]);

  const requestMoreAiPreviewChanges = useCallback(async (request) => {
    const pendingPreview = aiChangePreview;
    if (!pendingPreview?.onRefine) return;

    setAiOperation('refine-preview');
    setAiStatus('Preparing your revised preview...');
    try {
      const revision = await pendingPreview.onRefine(
        request,
        pendingPreview.afterDocument,
        pendingPreview.requestHistory || []
      );
      const revisedDocument = revision?.document?.root ? revision.document : revision;
      if (!revisedDocument) {
        throw new Error('The AI assistant did not return an updated preview.');
      }

      setAiChangePreview((currentPreview) => {
        if (currentPreview !== pendingPreview) return currentPreview;
        const revisionHasRecordUpdates = revision?.document?.root
          && revision.recordUpdatesSpecified;
        const revisedRecordUpdates = revisionHasRecordUpdates
          ? (revision.recordUpdates || EMPTY_AI_RECORD_UPDATES)
          : currentPreview.recordUpdates;
        const revisedRecordContext = revisionHasRecordUpdates
          ? (revision.recordContext || currentPreview.recordContext)
          : currentPreview.recordContext;
        return {
          ...currentPreview,
          afterDocument: revisedDocument,
          recordUpdates: revisedRecordUpdates,
          recordContext: revisedRecordContext,
          summary: withRecordUpdateSummary(
            getAiChangePreviewSummary(
              currentPreview.beforeDocument,
              revisedDocument,
              { focusedNodeId: currentPreview.focusedNodeId }
            ),
            revisedRecordUpdates,
            revisedRecordContext
          ),
          requestHistory: [...(currentPreview.requestHistory || []), request].slice(-5),
        };
      });
      setShowAiChangePreview(false);
    } catch (error) {
      if (isAiQuotaError(error)) setAiQuotaExceeded(true);
      throw error;
    } finally {
      setAiOperation('');
      setAiStatus('');
    }
  }, [aiChangePreview]);

  useEffect(() => {
    if (showAiChangePreview) setRootDragEnabled(false);
  }, [setRootDragEnabled, showAiChangePreview]);

  const generateWithAI = async ({
    regenerate = false,
    prompt,
    forceCompactEdit = false,
  } = {}) => {
    if (!edit || !aiEnabled || !jsonData) {
      return;
    }

    const trimmedPrompt = String(prompt || '').trim();
    if (!trimmedPrompt) {
      throw new Error('Describe what you would like the AI assistant to create.');
    }

    const replaceEntirePage = regenerate || (!hasPortfolioContent && !forceCompactEdit);
    setAiOperation(regenerate ? 'regenerate' : 'generate');
    setAiStatus('Gathering your portfolio details...');

    try {
      let context;
      let repairedDocument;
      let recordUpdates = EMPTY_AI_RECORD_UPDATES;
      let recordContext = { projects: [], skills: [] };

      if (replaceEntirePage) {
        setAiStatus('Gathering the details needed for the full page...');
        context = typeof aiMethods?.getGenerationContext === 'function'
          ? (await aiMethods.getGenerationContext({
            userId,
            includeProjects: FULL_PAGE_AI_CONTEXT_DECISION.includeProjects,
            includeSkills: FULL_PAGE_AI_CONTEXT_DECISION.includeSkills,
            includeJobs: true,
            includeImages: FULL_PAGE_AI_CONTEXT_DECISION.includeImages,
            hydrateImageDescriptions: false,
          })) || {}
          : {};
        setAiStatus(`Generating with ${describeAiContext(context)}...`);
        const generatedDocument = await generateFullPageStructure({
          prompt: trimmedPrompt,
          portfolio: jsonData,
          images: context.images || [],
          projects: context.projects || [],
          skills: context.skills || [],
          jobs: context.jobs || [],
          fallbackTitle: jsonData?.title || portfolioTitle,
          onRetry: (retryError) => setAiStatus(
            retryError?.code === 'AI_MAX_TOKENS'
              ? 'The page is large. Retrying with the next AI model...'
              : 'The first result was incomplete. Retrying with a more concise page...'
          ),
        });
        setAiStatus('Updating your projects and skills...');
        const hydratedDocument = await hydrateEditorDocumentCollections(generatedDocument, userId);
        setAiStatus('Preparing your new page...');
        repairedDocument = validateAndRepairEditorJson(hydratedDocument);
      } else {
        const result = await runGlobalAiEditWorkflow({
          request: trimmedPrompt,
          sourceDocument: editorDocument,
        });
        context = result.context;
        repairedDocument = result.document;
        recordUpdates = result.recordUpdates;
        recordContext = result.recordContext;
      }

      const contextWarning = context.warnings?.length
        ? ` Some details could not be loaded: ${context.warnings.join('; ')}.`
        : '';
      queueAiChangePreview({
        title: replaceEntirePage
          ? (regenerate ? 'Review regenerated portfolio' : 'Review generated portfolio')
          : 'Review portfolio edits',
        beforeDocument: editorDocument,
        afterDocument: repairedDocument,
        recordUpdates,
        recordContext,
        onApply: (approvedDocument) => {
          applyPortfolioState(withEditorDocument(jsonData, approvedDocument));
          if (contextWarning) {
            aiAssistant.addMessage('assistant', `The changes were applied.${contextWarning}`);
          }
        },
        requestHistory: [trimmedPrompt],
        onRefine: async (request, proposedDocument, previousRequests) => {
          const result = await runGlobalAiEditWorkflow({
            request,
            sourceDocument: proposedDocument,
            task: 'portfolio preview refinement',
            previousRequests,
          });
          return result;
        },
      });
      return repairedDocument;
    } catch (err) {
      console.error('AI generation error:', err);
      if (isAiQuotaError(err)) {
        setAiQuotaExceeded(true);
      }
      throw err;
    } finally {
      setAiOperation('');
      setAiStatus('');
    }
  };

  const generateSectionWithAI = useCallback(async (prompt, targetId = null, position = 'child') => {
    if (!edit || !aiEnabled || !jsonData || !editorDocument?.root) return;

    const trimmedPrompt = String(prompt || '').trim();
    if (!trimmedPrompt) throw new Error('Describe the section you would like to add.');

    setAiOperation('add-section');
    setAiStatus('Preparing a new section...');

    try {
      setAiStatus('Checking which portfolio details are needed...');
      const contextDecision = await needsAiPortfolioContext({
        prompt: trimmedPrompt,
        task: 'new portfolio section',
        selectedNode: findNodeById(editorDocument.root, targetId),
      });
      const sectionNeedsJobs = promptMentionsJobs(trimmedPrompt);
      const context = typeof aiMethods?.getGenerationContext === 'function'
        ? (await aiMethods.getGenerationContext({
          userId,
          includeProjects: contextDecision.includeProjects,
          includeSkills: contextDecision.includeSkills,
          includeJobs: sectionNeedsJobs,
          includeImages: contextDecision.includeImages,
          hydrateImageDescriptions: false,
        })) || {}
        : {};
      const selectedNode = findNodeById(editorDocument.root, targetId) || editorDocument.root;
      const generated = await generateChildElements({
        prompt: trimmedPrompt,
        selectedNode,
        editorDocument,
        images: context.images || [],
        projects: context.projects || [],
        skills: context.skills || [],
        jobs: context.jobs || [],
        onRetry: (error) => setAiStatus(
          error?.code === 'AI_MAX_TOKENS'
            ? 'The section is large. Retrying with the faster AI model...'
            : 'The first result was incomplete. Retrying with a smaller section...'
        ),
      });

      let nextRoot = editorDocument.root;
      let insertionTarget = targetId || editorDocument.root.id;
      const insertionPosition = targetId ? position : 'child';
      for (const child of generated.children || []) {
        nextRoot = insertNode(nextRoot, insertionTarget, child, insertionPosition);
        if (insertionPosition === 'after') insertionTarget = child.id;
      }

      const nextDocument = await hydrateEditorDocumentCollections({
        ...editorDocument,
        root: nextRoot,
        classes: { ...(editorDocument.classes || {}), ...(generated.classes || {}) },
      }, userId);
      const generatedSectionId = generated.children?.[0]?.id || null;
      queueAiChangePreview({
        title: 'Review new section',
        beforeDocument: editorDocument,
        afterDocument: nextDocument,
        onApply: (approvedDocument) => {
          setEditorDocument(approvedDocument);
          setNotice({ message: 'Your new section has been added.', variant: 'success' });
        },
        requestHistory: [trimmedPrompt],
        onRefine: generatedSectionId ? async (request, proposedDocument, previousRequests) => {
          const selectedSection = findNodeById(proposedDocument.root, generatedSectionId);
          if (!selectedSection) {
            throw new Error('The generated section is no longer available to update.');
          }
          const refinementContext = await loadAiRefinementContext({
            request,
            document: proposedDocument,
            selectedNode: selectedSection,
            task: 'generated portfolio section refinement',
          });
          const refinementRequest = buildAiPreviewRefinementRequest({
            request,
            previousRequests,
          });
          const revisedDocument = await editSelectedElement({
            prompt: refinementRequest,
            editorDocument: proposedDocument,
            selectedNodeId: generatedSectionId,
            images: refinementContext.images || [],
            projects: refinementContext.projects || [],
            skills: refinementContext.skills || [],
            jobs: refinementContext.jobs || [],
            onRetry: (retryError) => setAiStatus(
              retryError?.code === 'AI_MAX_TOKENS'
                ? 'The section is large. Retrying with the next AI model...'
                : 'The first result was incomplete. Retrying with a smaller edit...'
            ),
          });
          return hydrateEditorDocumentCollections(revisedDocument, userId);
        } : null,
      });
    } catch (error) {
      if (isAiQuotaError(error)) setAiQuotaExceeded(true);
      throw error;
    } finally {
      setAiOperation('');
      setAiStatus('');
    }
  }, [
    aiEnabled,
    aiMethods,
    edit,
    editorDocument,
    jsonData,
    loadAiRefinementContext,
    queueAiChangePreview,
    setEditorDocument,
    userId,
  ]);

  const editFocusedElementWithAI = async (prompt, targetNodeId) => {
    if (!edit || !aiEnabled || !jsonData || !targetNodeId) return;

    const targetNode = findNodeById(editorDocument?.root, targetNodeId);
    if (!targetNode) {
      throw new Error('The selected item is no longer available. Select it again and retry.');
    }
    const trimmedPrompt = String(prompt || '').trim();
    if (!trimmedPrompt) {
      throw new Error('Describe what you would like to change in the selected item.');
    }

    setAiOperation('focused-edit');
    setAiStatus('Gathering details for the selected item...');

    try {
      const focusedReferences = extractPortfolioReferences(targetNode);
      setAiStatus('Checking which portfolio details are needed...');
      const contextDecision = await needsAiPortfolioContext({
        prompt: trimmedPrompt,
        task: 'selected portfolio item edit',
        selectedNode: targetNode,
      });
      const needsProjects = contextDecision.includeProjects
        || contextDecision.modifyProjects
        || focusedReferences.referencedProjects.length > 0;
      const needsSkills = contextDecision.includeSkills
        || contextDecision.modifySkills
        || contextDecision.modifyProjects
        || focusedReferences.referencedSkills.length > 0;
      const needsJobs = promptMentionsJobs(trimmedPrompt);
      const context = typeof aiMethods?.getGenerationContext === 'function'
        ? (await aiMethods.getGenerationContext({
          userId,
          includeProjects: needsProjects,
          includeSkills: needsSkills,
          includeJobs: needsJobs,
          includeImages: contextDecision.includeImages,
          projectIds: contextDecision.includeProjects || contextDecision.modifyProjects
            ? undefined
            : focusedReferences.referencedProjects,
          skillIds: contextDecision.includeSkills || contextDecision.modifySkills || contextDecision.modifyProjects
            ? undefined
            : focusedReferences.referencedSkills,
          hydrateImageDescriptions: false,
        })) || {}
        : {};

      setAiStatus(`Updating the selected item using ${describeAiContext(context)}...`);
      const editedDocument = await editSelectedElement({
        prompt: trimmedPrompt,
        editorDocument,
        selectedNodeId: targetNodeId,
        images: context.images || [],
        projects: context.projects || [],
        skills: context.skills || [],
        jobs: context.jobs || [],
        onRetry: (error) => setAiStatus(
          error?.code === 'AI_MAX_TOKENS'
            ? 'The selected content is large. Retrying with the faster AI model...'
            : 'The first result was incomplete. Retrying with a smaller edit...'
        ),
      });
      setAiStatus('Checking and preparing your changes...');
      const hydratedDocument = await hydrateEditorDocumentCollections(editedDocument, userId);
      const repairedDocument = validateAndRepairEditorJson(hydratedDocument);
      const recordUpdatesSpecified = Boolean(
        contextDecision.modifyProjects || contextDecision.modifySkills
      );
      const recordUpdates = recordUpdatesSpecified
        ? await generatePortfolioRecordUpdates({
          prompt: trimmedPrompt,
          projects: context.projects || [],
          skills: context.skills || [],
          onRetry: () => setAiStatus('Retrying the saved project and skill changes...'),
        })
        : EMPTY_AI_RECORD_UPDATES;
      const recordContext = {
        projects: context.projects || [],
        skills: context.skills || [],
      };
      const contextWarning = context.warnings?.length
        ? ` Some details could not be loaded: ${context.warnings.join('; ')}.`
        : '';
      queueAiChangePreview({
        title: 'Review selected item changes',
        beforeDocument: editorDocument,
        afterDocument: repairedDocument,
        focusedNodeId: targetNodeId,
        recordUpdates,
        recordContext,
        onApply: (approvedDocument) => {
          applyPortfolioState(withEditorDocument(jsonData, approvedDocument));
          selectEditorNode(targetNodeId, { scroll: true });
          if (contextWarning) {
            aiAssistant.addMessage('assistant', `The selected item was updated.${contextWarning}`);
          }
        },
        requestHistory: [trimmedPrompt],
        onRefine: async (request, proposedDocument, previousRequests) => {
          const selectedNode = findNodeById(proposedDocument.root, targetNodeId);
          if (!selectedNode) {
            throw new Error('The selected item is no longer available to update.');
          }
          const refinementContext = await loadAiRefinementContext({
            request,
            document: proposedDocument,
            selectedNode,
            task: 'selected portfolio item preview refinement',
          });
          const refinementRequest = buildAiPreviewRefinementRequest({
            request,
            previousRequests,
          });
          const revisedDocument = await editSelectedElement({
            prompt: refinementRequest,
            editorDocument: proposedDocument,
            selectedNodeId: targetNodeId,
            images: refinementContext.images || [],
            projects: refinementContext.projects || [],
            skills: refinementContext.skills || [],
            jobs: refinementContext.jobs || [],
            onRetry: (retryError) => setAiStatus(
              retryError?.code === 'AI_MAX_TOKENS'
                ? 'The selected content is large. Retrying with the next AI model...'
                : 'The first result was incomplete. Retrying with a smaller edit...'
            ),
          });
          const hydratedRevision = await hydrateEditorDocumentCollections(revisedDocument, userId);
          const validatedRevision = validateAndRepairEditorJson(hydratedRevision);
          const revisionRecordUpdatesSpecified = Boolean(
            refinementContext.recordUpdateIntent?.modifyProjects
            || refinementContext.recordUpdateIntent?.modifySkills
          );
          const revisionRecordUpdates = revisionRecordUpdatesSpecified
            ? await generatePortfolioRecordUpdates({
              prompt: refinementRequest,
              projects: refinementContext.projects || [],
              skills: refinementContext.skills || [],
              onRetry: () => setAiStatus('Retrying the saved project and skill changes...'),
            })
            : EMPTY_AI_RECORD_UPDATES;
          return {
            document: validatedRevision,
            recordUpdates: revisionRecordUpdates,
            recordContext: {
              projects: refinementContext.projects || [],
              skills: refinementContext.skills || [],
            },
            recordUpdatesSpecified: revisionRecordUpdatesSpecified,
          };
        },
      });
      return repairedDocument;
    } catch (err) {
      console.error('Focused AI edit error:', err);
      if (isAiQuotaError(err)) {
        setAiQuotaExceeded(true);
      }
      throw err;
    } finally {
      setAiOperation('');
      setAiStatus('');
    }
  };

  const scoreWithAI = async () => {
    if (!edit || !aiEnabled || !jsonData) {
      return;
    }

    setAiOperation('score');
    setAiStatus('Finding portfolio evidence used for scoring...');

    try {
      const references = extractPortfolioReferences(jsonData);
      let context = {};
      if (typeof aiMethods?.getGenerationContext === 'function') {
        setAiStatus('Loading your selected jobs, projects, and skills...');
        context = (await aiMethods.getGenerationContext({
          userId,
          includeProjects: references.referencedProjects.length > 0,
          includeSkills: references.referencedSkills.length > 0,
          includeJobs: true,
          includeImages: references.referencedImages.length > 0,
          projectIds: references.referencedProjects,
          skillIds: references.referencedSkills,
          hydrateImageDescriptions: false,
          imageIds: references.referencedImages,
        })) || {};
      }

      if (!Array.isArray(context.jobs) || context.jobs.length === 0) {
        throw new Error('Select at least one target job before scoring job fit.');
      }

      setAiStatus('Preparing the most relevant portfolio details...');
      const scoringContext = buildPortfolioScoringContext({
        portfolioData: jsonData,
        projects: context.projects || [],
        skills: context.skills || [],
        jobs: context.jobs,
        images: context.images || [],
      });

      setAiStatus(`Comparing your page with ${scoringContext.stats.targetJobCount} selected job${scoringContext.stats.targetJobCount === 1 ? '' : 's'}...`);
      const rawScore = await requestPortfolioScoreWithRetry({
        modelNames: HTML_EDITOR_AI_MODEL_NAMES,
        requestScore: (modelName) => scorePortfolioAgainstChecklist({
          portfolioData: scoringContext.payload,
          checklist: TARGET_JOB_PORTFOLIO_SCORE_CHECKLIST,
          projects: scoringContext.scorerIndex.projects,
          skills: scoringContext.scorerIndex.skills,
          jobs: scoringContext.scorerIndex.jobs,
          images: scoringContext.scorerIndex.images,
          modelName,
          checklistExplanationField: 'description',
        }),
        onRetry: () => setAiStatus('The first response was incomplete. Asking the AI assistant to try once more...'),
      });
      setAiStatus('Preparing your feedback...');
      const normalizedScore = normalizePortfolioScoreResult(rawScore);
      const storedScore = createStoredPortfolioScore(normalizedScore);

      // Score metadata is independent from editor content and undo history.
      setJsonData((current) => current
        ? { ...current, [PORTFOLIO_SCORE_FIELD]: storedScore }
        : current);

      if (!isNew && typeof updatePortfolio === 'function') {
        setAiStatus('Saving your latest score...');
        try {
          await updatePortfolio(userId, portfolioTitle, {
            [PORTFOLIO_SCORE_FIELD]: storedScore,
          });
        } catch (scoreSaveError) {
          console.error('Error saving portfolio score:', scoreSaveError);
          setNotice({
            variant: 'warning',
            message: 'Your score is ready, but it could not be saved to the portfolio list.',
          });
        }
      }

      return normalizedScore;
    } catch (err) {
      console.error('AI scoring error:', err);
      if (isAiQuotaError(err)) {
        setAiQuotaExceeded(true);
      }
      throw err;
    } finally {
      setAiOperation('');
      setAiStatus('');
    }
  };

  const closeAiAssistant = useCallback(() => {
    setShowAIMenu(false);
    aiAssistant.close();
    setRootDragEnabled(true);
    aiInvokerRef.current?.focus?.();
  }, [aiAssistant, setRootDragEnabled]);

  const executeAssistantTask = async (task, prompt = '', { recordUser = true } = {}) => {
    if (!task || aiBusy || aiQuotaExceeded) {
      return { ok: false, error: 'The AI assistant is not ready for another request.' };
    }

    const safePrompt = String(redactBase64FromAiPrompt(String(prompt || '')) || '').trim();
    if (recordUser && safePrompt) {
      aiAssistant.addMessage('user', safePrompt, { taskType: task.type });
      aiAssistant.clearDraft();
    }

    if (isGuidedAiTask(task.type)) {
      aiAssistant.setPhase(
        task.type === AI_TASK_TYPES.REFINE_PREVIEW
          ? AI_WORKFLOW_PHASES.REFINE
          : AI_WORKFLOW_PHASES.WORKING,
        {
          submittedRequest: safePrompt,
          error: '',
        }
      );
    }

    try {
      switch (task.type) {
        case AI_TASK_TYPES.GENERATE_PAGE:
          await generateWithAI({ prompt: safePrompt });
          aiAssistant.addMessage('assistant', 'Your new portfolio is ready to review.');
          break;
        case AI_TASK_TYPES.GLOBAL_EDIT:
          await generateWithAI({ prompt: safePrompt, forceCompactEdit: true });
          aiAssistant.addMessage('assistant', 'Your portfolio edits are ready to review.');
          break;
        case AI_TASK_TYPES.REGENERATE_PAGE:
          await generateWithAI({ prompt: safePrompt, regenerate: true });
          aiAssistant.addMessage('assistant', 'The regenerated portfolio is ready to review.');
          break;
        case AI_TASK_TYPES.FOCUSED_EDIT:
          if (!findNodeById(editorDocument?.root, task.nodeId)) {
            throw new Error('That item is no longer on the page. Select it again before asking AI to edit it.');
          }
          await editFocusedElementWithAI(safePrompt, task.nodeId);
          aiAssistant.addMessage('assistant', 'The selected item changes are ready to review.');
          break;
        case AI_TASK_TYPES.ADD_SECTION:
          if (task.targetId && !findNodeById(editorDocument?.root, task.targetId)) {
            throw new Error('That insertion point is no longer available. Open Add Content again and retry.');
          }
          await generateSectionWithAI(safePrompt, task.targetId || null, task.position || 'child');
          aiAssistant.addMessage('assistant', 'Your new section is ready to review.');
          break;
        case AI_TASK_TYPES.SCORE_PORTFOLIO: {
          const result = await scoreWithAI();
          if (result) {
            aiAssistant.addMessage('assistant', '', {
              taskType: task.type,
              payload: createPortfolioScoreMessagePayload(result),
            });
          }
          aiAssistant.completeRun('Portfolio scored');
          break;
        }
        case AI_TASK_TYPES.IMPLEMENT_RECOMMENDATION: {
          const recommendationPrompt = safePrompt;
          await generateWithAI({
            prompt: recommendationPrompt,
            forceCompactEdit: true,
          });
          aiAssistant.addMessage('assistant', 'That recommendation is ready to review.');
          break;
        }
        case AI_TASK_TYPES.REFINE_PREVIEW:
          if (!aiChangePreview?.onRefine || task.previewId !== aiChangePreview.id) {
            throw new Error('That preview is no longer available. Create a new AI change and try again.');
          }
          await requestMoreAiPreviewChanges(safePrompt);
          aiAssistant.setActiveTask({ ...task, showComposer: false }, { draft: '' });
          aiAssistant.setPhase(AI_WORKFLOW_PHASES.REVIEW, { error: '' });
          aiAssistant.addMessage('assistant', 'The revised changes are ready to review.');
          break;
        default:
          throw new Error('This AI action is not available.');
      }
      return { ok: true };
    } catch (taskError) {
      const message = isAiQuotaError(taskError)
        ? 'Today\'s free AI allowance has been used. Please try again after it resets.'
        : taskError?.message || 'The AI assistant could not complete that request. Please try again.';
      if (isAiQuotaError(taskError)) setAiQuotaExceeded(true);
      if (isGuidedAiTask(task.type)) {
        aiAssistant.setActiveTask(
          { ...task, showComposer: true },
          { draft: safePrompt }
        );
        aiAssistant.setPhase(AI_WORKFLOW_PHASES.ERROR, {
          submittedRequest: safePrompt,
          error: message,
        });
      }
      aiAssistant.addMessage('error', message, {
        taskType: task.type,
        retry: { task, prompt: safePrompt },
      });
      return { ok: false, error: message };
    }
  };

  const requestAssistantConfirmation = ({ confirmation, task, prompt }) => {
    const confirmationId = `ai-confirmation-${Date.now()}`;
    aiAssistant.addMessage('assistant', confirmation.message, {
      taskType: task.type,
      payload: {
        kind: 'confirmation',
        confirmationId,
        title: confirmation.title,
      },
    });
    setAiConfirmation({
      ...confirmation,
      id: confirmationId,
      task,
      prompt,
    });
    aiAssistant.setPhase(AI_WORKFLOW_PHASES.CONFIRM, {
      submittedRequest: prompt,
      error: '',
    });
  };

  const submitAssistantPrompt = (prompt) => {
    const task = aiComposerTask;
    if (!task) return;
    const safePrompt = String(redactBase64FromAiPrompt(String(prompt || '')) || '').trim();
    if (!safePrompt) return;

    if (task.type === AI_TASK_TYPES.REFINE_PREVIEW) {
      void executeAssistantTask(task, safePrompt).then((result) => {
        if (result?.ok) {
          setAiComposerTask(createAiTask(AI_TASK_TYPES.GLOBAL_EDIT));
          setAiComposerDraft('');
        }
      });
      return;
    }
    aiRequestQueue.enqueue(task, safePrompt);
    setAiComposerDraft('');
    setAiComposerTask(createAiTask(AI_TASK_TYPES.GLOBAL_EDIT));
  };

  const confirmAiDecision = async () => {
    const pendingConfirmation = aiConfirmation;
    if (!pendingConfirmation) return;
    setAiConfirmation(null);
    aiAssistant.setPhase(AI_WORKFLOW_PHASES.WORKING, { error: '' });
    aiAssistant.addMessage('assistant', 'Confirmed. I am starting that change now.');
    const requestId = aiRequestQueue.activeRequest?.id;
    if (requestId) {
      aiRequestQueue.setStatus(requestId, AI_QUEUE_STATUS.RUNNING, { confirmed: true });
    }
  };

  const rejectAiDecision = () => {
    const pendingConfirmation = aiConfirmation;
    if (!pendingConfirmation) return;
    setAiConfirmation(null);
    aiAssistant.clearDraft();
    aiAssistant.addMessage('assistant', 'That request was cancelled. Your portfolio was not changed.');
    aiAssistant.completeRun('Change cancelled');
    if (aiRequestQueue.activeRequest?.id) {
      aiRequestQueue.complete(aiRequestQueue.activeRequest.id);
    }
  };

  const chooseAssistantTask = (taskType) => {
    if (taskType === AI_TASK_TYPES.FOCUSED_EDIT) {
      if (!focusedNodeId || !findNodeById(editorDocument?.root, focusedNodeId)) {
        aiAssistant.addMessage('error', 'Select an item on the page before asking AI to edit it.');
        return;
      }
      openFocusedAiMenu(focusedNodeId);
      return;
    }
    const task = createAiTask(taskType);
    openAssistantTask(task);
    if (taskType === AI_TASK_TYPES.SCORE_PORTFOLIO) {
      aiRequestQueue.enqueue(task, '');
      setAiComposerTask(createAiTask(AI_TASK_TYPES.GLOBAL_EDIT));
    }
  };

  const implementScoreRecommendation = (recommendation, source = {}) => {
    const alreadyQueued = aiRequestQueue.requests.some((request) => (
      request.task?.type === AI_TASK_TYPES.IMPLEMENT_RECOMMENDATION
      && request.task.scoreMessageId === source?.scoreMessageId
      && request.task.recommendationIndex === source?.recommendationIndex
    ));
    if (alreadyQueued) return;
    const recommendationPrompt = buildScoreRecommendationEditPrompt(recommendation);
    const task = createAiTask(AI_TASK_TYPES.IMPLEMENT_RECOMMENDATION, {
      scoreMessageId: source?.scoreMessageId,
      recommendationIndex: source?.recommendationIndex,
      contextLabel: recommendation?.title || recommendation?.label || 'Score recommendation',
    });
    openAssistantTask(task);
    aiRequestQueue.enqueue(task, recommendationPrompt);
    setAiComposerTask(createAiTask(AI_TASK_TYPES.GLOBAL_EDIT));
  };

  const retryAssistantTask = ({ task, prompt }) => {
    if (!task) return;
    openAssistantTask(task, { draft: '' });
    aiRequestQueue.enqueue(task, prompt);
    setAiComposerTask(createAiTask(AI_TASK_TYPES.GLOBAL_EDIT));
  };

  const openAddSectionAssistant = useCallback((targetId = null, position = 'child') => {
    const targetNode = targetId ? findNodeById(editorDocument?.root, targetId) : editorDocument?.root;
    if (!targetNode) {
      aiAssistant.addMessage('error', 'That insertion point is no longer available. Open Add Content again.');
      return;
    }
    openAssistantTask(createAiTask(AI_TASK_TYPES.ADD_SECTION, {
      targetId,
      position,
      contextLabel: targetId
        ? `Add ${position === 'after' ? 'below' : 'inside'} ${
          editorMode === EDITOR_MODES.SIMPLE
            ? getFriendlyEditorNodeName(targetNode)
            : `<${targetNode.type}>`
        }`
        : 'Add to page',
    }));
  }, [aiAssistant, editorDocument?.root, editorMode, openAssistantTask]);

  const openPreviewRefinement = useCallback(() => {
    if (!aiChangePreview?.onRefine) return;
    setShowAiChangePreview(false);
    setShowAIMenu(true);
    setAiComposerTask(createAiTask(AI_TASK_TYPES.REFINE_PREVIEW, {
      previewId: aiChangePreview.id,
      contextLabel: aiChangePreview.title,
      showComposer: true,
      scoreMessageId: aiAssistant.activeTask?.scoreMessageId,
      recommendationIndex: aiAssistant.activeTask?.recommendationIndex,
    }));
    setAiComposerDraft('');
    aiAssistant.setPhase(AI_WORKFLOW_PHASES.REFINE, {
      previewId: aiChangePreview.id,
      error: '',
    });
  }, [aiAssistant, aiChangePreview]);

  useEffect(() => {
    if (
      aiBusy
      || aiChangePreview
      || aiConfirmation
      || aiRequestQueue.activeRequest
      || !aiRequestQueue.nextRequest
    ) return;
    aiRequestQueue.start(aiRequestQueue.nextRequest.id);
  }, [
    aiBusy,
    aiChangePreview,
    aiConfirmation,
    aiRequestQueue,
  ]);

  useEffect(() => {
    const request = aiRequestQueue.activeRequest;
    if (!request || request.status !== AI_QUEUE_STATUS.RUNNING || aiBusy) return;
    if (executingQueuedRequestRef.current === request.id) return;
    executingQueuedRequestRef.current = request.id;

    if (request.task.type === AI_TASK_TYPES.REGENERATE_PAGE && !request.confirmed) {
      aiAssistant.openTask(request.task, { restart: true });
      requestAssistantConfirmation({
        confirmation: createFullPageRegenerationConfirmation(),
        task: request.task,
        prompt: request.prompt,
      });
      aiRequestQueue.setStatus(request.id, AI_QUEUE_STATUS.AWAITING_CONFIRMATION);
      executingQueuedRequestRef.current = null;
      return;
    }

    if (!request.confirmed) {
      aiAssistant.openTask(request.task, { restart: true });
    }
    const recordUser = ![
      AI_TASK_TYPES.SCORE_PORTFOLIO,
      AI_TASK_TYPES.IMPLEMENT_RECOMMENDATION,
    ].includes(request.task.type);

    void executeAssistantTask(request.task, request.prompt, { recordUser })
      .then((result) => {
        if (!result?.ok) {
          aiRequestQueue.setStatus(request.id, AI_QUEUE_STATUS.FAILED, {
            error: result?.error || 'The request could not be completed.',
          });
          return;
        }
        if (AI_TASKS_REQUIRING_REVIEW.has(request.task.type)) {
          aiRequestQueue.setStatus(request.id, AI_QUEUE_STATUS.AWAITING_REVIEW);
        } else {
          aiRequestQueue.complete(request.id);
        }
      })
      .finally(() => {
        executingQueuedRequestRef.current = null;
      });
    // executeAssistantTask intentionally uses the latest editor document at dispatch time.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aiBusy, aiRequestQueue.activeRequest?.id, aiRequestQueue.activeRequest?.status]);

  const updateQueuedAssistantRequest = useCallback((requestId, values) => {
    let task = values.task;
    if (task?.type === AI_TASK_TYPES.FOCUSED_EDIT) {
      task = createFocusedAiTask(focusedNodeId);
      if (!task) return;
    } else {
      const current = aiRequestQueue.requests.find((request) => request.id === requestId);
      task = current?.task?.type === task?.type
        ? { ...current.task, ...task }
        : createAiTask(task?.type);
    }
    if (aiRequestQueue.activeRequest?.id === requestId) {
      setAiConfirmation(null);
      aiAssistant.completeRun('Request changed');
    }
    aiRequestQueue.update(requestId, { ...values, task });
  }, [
    aiAssistant,
    aiRequestQueue,
    createFocusedAiTask,
    focusedNodeId,
  ]);

  const removeQueuedAssistantRequest = useCallback((requestId) => {
    if (aiRequestQueue.activeRequest?.id === requestId) {
      setAiConfirmation(null);
      aiAssistant.completeRun('Request removed');
    }
    aiRequestQueue.remove(requestId);
  }, [aiAssistant, aiRequestQueue]);

  const moveQueuedAssistantRequest = useCallback((requestId, targetId) => {
    if (aiConfirmation && aiRequestQueue.activeRequest?.id === requestId) {
      setAiConfirmation(null);
      aiAssistant.completeRun('Request reordered');
    }
    aiRequestQueue.move(requestId, targetId);
  }, [aiAssistant, aiConfirmation, aiRequestQueue]);

  const moveQueuedAssistantRequestBy = useCallback((requestId, offset) => {
    if (aiConfirmation && aiRequestQueue.activeRequest?.id === requestId) {
      setAiConfirmation(null);
      aiAssistant.completeRun('Request reordered');
    }
    aiRequestQueue.moveBy(requestId, offset);
  }, [aiAssistant, aiConfirmation, aiRequestQueue]);

  const rendererActions = createRendererActions({
    items: rootChildren,
    setItems: setRootContent,
    edit,
    captureItemPositions,
  });

  return (
    <>
      {isLoading ? (
        <div
          className="d-flex min-vh-100 align-items-center justify-content-center"
          role="status"
          aria-live="polite"
        >
          <div className="spinner-border text-primary" aria-hidden="true" />
          <span className="visually-hidden">Loading portfolio...</span>
        </div>
      ) : error ? (
        <div className="container text-center">
          <p>Error: {error}</p>
          {edit && (
            <button className="btn btn-primary mt-3" onClick={() => navigate('/dashboard')}>
              Back to Dashboard
            </button>
          )}
        </div>
      ) : (
        <>
          <GoogleFontLoader classes={customClasses} previewFamilies={fontPreviewFamilies} />
          {customCss ? <style>{customCss}</style> : null}
          <RootCanvasElement
            {...rootProps}
            ref={canvasRef}
            className={resolvedRootCanvasClassName}
            onClick={edit ? handleCanvasClick : openPublishedProject}
            {...(edit
              ? { onDoubleClick: handleCanvasDoubleClick }
              : { onKeyDown: openPublishedProjectFromKeyboard })}
          >
            {rootChildren.length === 0 ? (
              <div className="html-editor-empty text-center">
                <p>This page is empty. Add some content to get started.</p>
                {edit ? (
                  <div className="d-flex flex-wrap justify-content-center gap-2">
                    <button
                      className="btn btn-primary html-editor-empty-add"
                      type="button"
                      onClick={(event) => {
                        event.stopPropagation();
                        setShowAddMenu(true);
                        setEditPanelNodeId(null);
                        setShowClassStyleMenu(false);
                        setShowAIMenu(false);
                        setLayerMoveTargetId(null);
                        setRootDragEnabled(false);
                      }}
                    >
                      <i className="bi bi-plus-lg me-2" aria-hidden="true"></i>
                      Add content
                    </button>
                    {aiEnabled ? (
                      <button
                        className="btn btn-outline-primary"
                        type="button"
                        disabled={aiQuotaExceeded}
                        onClick={(event) => {
                          event.stopPropagation();
                          openAssistantTask(createAiTask(AI_TASK_TYPES.GENERATE_PAGE));
                        }}
                      >
                        <i className="bi bi-stars me-2" aria-hidden="true"></i>Create with AI
                      </button>
                    ) : null}
                  </div>
                ) : null}
              </div>
            ) : rootChildren.map((item, index) => {
              const itemKey = item.id;
              const dragClassName = rootDragSupported ? getDragClassName(index) : '';

              return (
                <Renderer
                  key={itemKey}
                  item={item}
                  index={index}
                  itemKey={itemKey}
                  edit={edit}
                  dragEnabled={deviceCanDrag && rootDragEnabled}
                  dragClassName={dragClassName}
                  setItemRef={rootDragSupported ? setItemRef : undefined}
                  onDragOver={rootDragSupported ? onDragOver : undefined}
                  onDrop={rootDragSupported
                    ? (event, targetIndex) => onDrop(event, targetIndex, rendererActions.moveItem)
                    : undefined}
                  renderEditItem={(currentItem, currentIndex) => (
                    isTextNode(currentItem) ? (
                      <span className="html-text-node">{currentItem.content}</span>
                    ) : (
                      <EditItem
                        index={currentIndex}
                        siblingCount={rootChildren.length}
                        item={currentItem}
                        addItem={rendererActions.addItem}
                        swapItems={rendererActions.swapItems}
                        editJSONItem={rendererActions.editJSONItem}
                        removeItem={rendererActions.removeItem}
                        focusedNodeId={focusedNodeId}
                        editPanelNodeId={editPanelNodeId}
                        activeEditorPathIds={activeEditorPathIds}
                        compactActions={compactElementActions}
                        onOpenPhoneRearrange={openPhoneRearrangeMenu}
                        userId={userId}
                        setEditPanelNodeId={setEditPanelNodeId}
                        setDrag={setRootDragEnabled}
                        dragSupported={edit && deviceCanDrag}
                        dragEnabled={edit && deviceCanDrag && rootDragEnabled}
                        onRootDragStart={(event) => onDragStart(event, currentIndex, currentItem.id)}
                        onRootDragEnd={onDragEnd}
                        onMoveNode={moveEditorNode}
                        onUpdateNode={updateEditorNode}
                        onInsertNode={insertEditorNode}
                        onRemoveNode={removeEditorNode}
                        onReorderNode={reorderEditorNode}
                        layerMoveOptions={focusedLayerMoveOptions}
                        onMoveUpLayer={moveEditorNodeUpLayer}
                        onOpenLayerMoveMenu={openLayerMoveMenu}
                        editorDocument={editorDocument}
                        setEditorDocument={setEditorDocument}
                        disallowedItems={disallowedItems}
                        resolvedCustomClasses={customClasses}
                        requestConfirmation={requestConfirmation}
                        aiEnabled={aiEnabled && !aiQuotaExceeded}
                        onOpenAISection={openAddSectionAssistant}
                        editorMode={editorMode}
                        onOpenClassStyleMenu={(className = '') => {
                          setClassStylePreferredClass(className);
                          setEditPanelNodeId(null);
                          setShowAddMenu(false);
                          setShowAIMenu(false);
                          setLayerMoveTargetId(null);
                          setShowClassStyleMenu(true);
                          setRootDragEnabled(false);
                        }}
                      />
                    )
                  )}
                  renderViewItem={(currentItem) => <ViewItem item={currentItem} />}
                />
              );
            })}
          </RootCanvasElement>

          {!edit && openProject ? (
            <ProjectDetailModal project={openProject} onClose={() => setOpenProjectId(null)} />
          ) : null}

          {edit ? <div className="html-editor-bottom-space" aria-hidden="true"></div> : null}

          {edit && (
            <>
              <EditorModeToggle
                editorMode={editorMode}
                onChange={changeEditorMode}
                onBack={() => window.history.back()}
              />

              <SelectionNavigator
                context={selectionContext}
                editorMode={editorMode}
                showElementActions={compactElementActions}
                onSelect={(nodeId) => {
                  selectEditorNode(nodeId, { scroll: true });
                }}
              />

              <EditButtons
                saveToJSON={() => {
                  if (!edit) return;
                  requestConfirmation({
                    title: 'Save portfolio?',
                    message: 'Your latest changes will replace the current saved version.',
                    confirmLabel: 'Save portfolio',
                    busyLabel: 'Saving...',
                    onConfirm: async () => {
                      if (userId !== getCurrentUserUID()) {
                        setSaveFailed(true);
                        setError('Please sign in before saving your portfolio.');
                        return;
                      }

                      const dataToSave = withEditorDocument(jsonData, editorDocument);
                      const result = await savePortfolioData(dataToSave);
                      if (result.success) {
                        setNotice({ message: 'Portfolio saved successfully.', variant: 'success' });
                      } else {
                        setError(result.error || 'We could not save your portfolio. Please try again.');
                      }
                    },
                  });
                }}
                setShowClassStyleMenu={(value) => {
                  setShowClassStyleMenu(value);
                  if (value) {
                    setClassStylePreferredClass('');
                    setEditPanelNodeId(null);
                    setShowAddMenu(false);
                    setShowAIMenu(false);
                    setLayerMoveTargetId(null);
                    setRootDragEnabled(false);
                  } else {
                    setRootDragEnabled(true);
                  }
                }}
                showClassStyleMenu={showClassStyleMenu}
                setShowFontMenu={(value) => {
                  setShowFontMenu(value);
                  if (value) {
                    setShowAddMenu(false);
                    setShowClassStyleMenu(false);
                    setShowAIMenu(false);
                    setEditPanelNodeId(null);
                    setLayerMoveTargetId(null);
                    setRootDragEnabled(false);
                  } else {
                    setFontPreviewFamilies([]);
                    setRootDragEnabled(true);
                  }
                }}
                showFontMenu={showFontMenu}
                aiEnabled={aiEnabled}
                selectedItemLabel={focusedItemLabel}
                onOpenAI={openContextualAiAssistant}
                onUndo={onUndo}
                onRedo={onRedo}
                canUndo={historyPast.length > 0}
                canRedo={historyFuture.length > 0}
                aiQuotaExceeded={aiQuotaExceeded}
                aiQueueCount={aiRequestQueue.requests.length}
                aiQueueWorking={aiRequestQueue.activeRequest?.status === AI_QUEUE_STATUS.RUNNING}
                aiQueueNeedsAttention={aiRequestQueue.needsAttention}
                editorMode={editorMode}
                saveStatus={saveStatus}
              />

              {aiEnabled && renderAiMenu ? (
                <PortfolioAssistantMenu
                  expanded={showAIMenu}
                  task={aiComposerTask}
                  run={aiAssistant.activeRun}
                  runs={aiAssistant.runs}
                  messages={aiAssistant.messages}
                  draft={aiComposerDraft}
                  busy={aiBusy}
                  status={aiStatus}
                  quotaExceeded={aiQuotaExceeded}
                  preview={aiChangePreview}
                  confirmation={aiConfirmation}
                  queueRequests={aiRequestQueue.requests}
                  selectedItemLabel={focusedItemLabel}
                  onClose={closeAiAssistant}
                  onDraftChange={setAiComposerDraft}
                  onSubmit={submitAssistantPrompt}
                  onRetry={retryAssistantTask}
                  onChooseTask={chooseAssistantTask}
                  onImplementRecommendation={implementScoreRecommendation}
                  onExpandPreview={() => setShowAiChangePreview(true)}
                  onConfirmPreview={applyAiChangePreview}
                  onEditPreview={openPreviewRefinement}
                  onDiscardPreview={discardAiChangePreview}
                  onReturnToReview={() => {
                    aiAssistant.returnToReview();
                    setAiComposerTask(createAiTask(AI_TASK_TYPES.GLOBAL_EDIT));
                    setAiComposerDraft('');
                  }}
                  onConfirmDecision={confirmAiDecision}
                  onRejectDecision={rejectAiDecision}
                  onQueueUpdate={updateQueuedAssistantRequest}
                  onQueueRemove={removeQueuedAssistantRequest}
                  onQueueRetry={aiRequestQueue.retry}
                  onQueueMove={moveQueuedAssistantRequest}
                  onQueueMoveBy={moveQueuedAssistantRequestBy}
                />
              ) : null}

              {editorMode === EDITOR_MODES.ADVANCED ? <LayerMoveMenu
                expanded={Boolean(layerMoveTargetId)}
                setExpanded={(value) => {
                  if (!value) closeLayerMoveMenu();
                }}
                targetLabel={layerMoveOptions.nodeLabel || 'element'}
                parentCandidates={layerMoveOptions.parentCandidates}
                onMove={moveEditorNodeDownLayer}
              /> : null}

              {compactElementActions ? (
                <PhoneRearrangeMenu
                  expanded={showPhoneRearrange}
                  setExpanded={(value) => {
                    if (!value) closePhoneRearrangeMenu();
                  }}
                  root={editorDocument?.root}
                  selectedNodeId={focusedNodeId}
                  editorMode={editorMode}
                  onSelect={(nodeId) => selectEditorNode(nodeId)}
                  onReorder={reorderPhoneEditorNode}
                  onMoveOut={moveEditorNodeUpLayer}
                  onMoveInto={movePhoneEditorNodeInto}
                />
              ) : null}

              {editorMode === EDITOR_MODES.ADVANCED ? <ClassStyleMenu
                expanded={showClassStyleMenu}
                setExpanded={(value) => {
                  setShowClassStyleMenu(value);
                  if (!value) setRootDragEnabled(true);
                }}
                editorDocument={editorDocument}
                setEditorDocument={setEditorDocument}
                preferredClassName={classStylePreferredClass}
                requestConfirmation={requestConfirmation}
                onPreviewFonts={setFontPreviewFamilies}
              /> : null}

              <FontMenu
                expanded={showFontMenu}
                setExpanded={(value) => {
                  setShowFontMenu(value);
                  if (!value) {
                    setFontPreviewFamilies([]);
                    setRootDragEnabled(true);
                  }
                }}
                editorDocument={editorDocument}
                setEditorDocument={setEditorDocument}
                onPreviewFonts={setFontPreviewFamilies}
              />

              <AddItemsMenu
                onAddItem={rendererActions.addItem}
                setShowAddMenu={(value) => {
                  setShowAddMenu(value);
                  if (!value) setRootDragEnabled(true);
                }}
                showAddMenu={showAddMenu}
                userId={userId}
                disallowedItems={disallowedItems}
                requestConfirmation={requestConfirmation}
                editorMode={editorMode}
                aiEnabled={aiEnabled && !aiQuotaExceeded}
                onOpenAISection={() => openAddSectionAssistant()}
              />
            </>
          )}
        </>
      )}
      {aiChangePreview && showAiChangePreview ? (
        <AiChangePreview
          preview={aiChangePreview}
          viewOnly
          onClose={() => setShowAiChangePreview(false)}
        />
      ) : null}
      <ConfirmationDialog {...confirmationProps} />
      <FeedbackNotice
        message={notice?.message || ''}
        variant={notice?.variant || 'info'}
        onDismiss={() => setNotice(null)}
      />
    </>
  );
};

export default BlogRenderer;
